import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Check, X, ImageIcon, Euro, Ruler } from 'lucide-react'
import type { OnboardingFlash } from '@/types/models'

interface FlashGalleryProps {
  flashes: OnboardingFlash[]
  selectedFlashIds: string[]
  onSelectionChange: (selectedIds: string[]) => void
  readOnly?: boolean
}

export function FlashGallery({ 
  flashes, 
  selectedFlashIds, 
  onSelectionChange,
  readOnly = false
}: FlashGalleryProps) {
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<string>>(new Set())

  const handleImageError = (flashId: string) => {
    setImageLoadErrors(prev => new Set(prev).add(flashId))
  }

  const toggleFlashSelection = (flashId: string) => {
    if (readOnly) return
    
    const isSelected = selectedFlashIds.includes(flashId)
    
    if (isSelected) {
      // Remove from selection
      onSelectionChange(selectedFlashIds.filter(id => id !== flashId))
    } else {
      // Add to selection
      onSelectionChange([...selectedFlashIds, flashId])
    }
  }

  const selectAll = () => {
    if (readOnly) return
    const validFlashIds = flashes
      .filter(flash => !imageLoadErrors.has(flash.id))
      .map(flash => flash.id)
    onSelectionChange(validFlashIds)
  }

  const deselectAll = () => {
    if (readOnly) return
    onSelectionChange([])
  }

  const selectedCount = selectedFlashIds.length
  const totalCount = flashes.length

  if (flashes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Flash Designs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                <ImageIcon className="w-6 h-6 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium">No flash designs found</p>
                <p className="text-sm">Artist hasn't uploaded any flash designs yet</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Flash Designs</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              {selectedCount} of {totalCount} selected
            </Badge>
          </div>
        </div>
        {!readOnly && (
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={selectAll}
              disabled={selectedCount === totalCount}
            >
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={deselectAll}
              disabled={selectedCount === 0}
            >
              Deselect All
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {flashes.map((flash) => {
            const isSelected = selectedFlashIds.includes(flash.id)
            const hasError = imageLoadErrors.has(flash.id)
            
            if (hasError) {
              return (
                <div
                  key={flash.id}
                  className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-sm"
                >
                  Failed to load
                </div>
              )
            }

            return (
              <div
                key={flash.id}
                className={`relative aspect-square rounded-lg overflow-hidden ${
                  readOnly ? '' : 'cursor-pointer'
                } border-2 transition-all ${
                  isSelected 
                    ? 'border-primary shadow-lg' 
                    : 'border-transparent hover:border-gray-300'
                }`}
                onClick={() => toggleFlashSelection(flash.id)}
              >
                <img
                  src={flash.imageUrl}
                  alt={`Flash design ${flash.id}`}
                  className="w-full h-full object-cover"
                  onError={() => handleImageError(flash.id)}
                  loading="lazy"
                />
                
                {/* Selection overlay */}
                {!readOnly && (
                  <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${
                    isSelected ? 'bg-black/30' : 'bg-black/0 hover:bg-black/10'
                  }`}>
                    {isSelected && (
                      <div className="bg-primary text-primary-foreground rounded-full p-2">
                        <Check className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                )}
                
                {/* Selection indicator in corner */}
                {!readOnly && (
                  <div className={`absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    isSelected 
                      ? 'bg-primary border-primary text-primary-foreground' 
                      : 'bg-white border-gray-300'
                  }`}>
                    {isSelected && <Check className="w-3 h-3" />}
                  </div>
                )}

                {/* Flash details overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                  <div className="flex items-center gap-2 text-white text-xs">
                    <div className="flex items-center gap-1">
                      <Euro className="w-3 h-3" />
                      <span>{flash.minPrice}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Ruler className="w-3 h-3" />
                      <span>{flash.minSize}</span>
                    </div>
                  </div>
                  {flash.cropValidation && (
                    <div className="mt-1">
                      <Badge 
                        variant={flash.cropValidation.isValid ? "default" : "destructive"}
                        className="text-xs"
                      >
                        {flash.cropValidation.isValid ? 'Valid' : 'Invalid'} Crop
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Selected flashes summary */}
        {selectedCount > 0 && !readOnly && (
          <div className="mt-6 p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">
              Selected flash designs ({selectedCount}):
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedFlashIds.map((flashId) => {
                const flash = flashes.find(f => f.id === flashId)
                if (!flash) return null
                
                return (
                  <div
                    key={flashId}
                    className="relative group"
                  >
                    <img
                      src={flash.imageUrl}
                      alt={`Selected flash ${flashId}`}
                      className="w-12 h-12 object-cover rounded border"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleFlashSelection(flashId)
                      }}
                      className="absolute -top-1 -right-1 bg-destructive text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-2 h-2" />
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
